"use client"

import type { FormEvent } from "react"
import { sectionStyle } from "../lib/section"
import type { FormProps, FormField } from "./form"

export type { FormProps, FormField } from "./form"

export type FormBodyProps = FormProps & {
  /** Client wrapper passes a real submit; the editor render leaves it off. */
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void
  submitting?: boolean
  status?: "idle" | "success" | "error"
}

/**
 * Shared presentational body for the Form block. The block's render uses it
 * directly; the storefront wraps it in a client component that wires the
 * submit handler to the backend (site-form.tsx there).
 *
 * Not marked "use client": the package renders under RSC on the storefront
 * and the client editor imports the same file. The optional onSubmit keeps
 * the JSX server-safe because a function prop is only ever attached inside a
 * client boundary.
 */
export function FormBody({
  eyebrow,
  title,
  subtitle,
  submitLabel,
  action,
  fields,
  successMessage,
  background,
  paddingY,
  onSubmit,
  submitting,
  status,
}: FormBodyProps) {
  const s = sectionStyle({ background, paddingY })
  return (
    <section style={s}>
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "0 24px" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          {eyebrow && (
            <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--site-accent, #7c3aed)", marginBottom: 12 }}>
              {eyebrow}
            </p>
          )}
          <h2 style={{ fontSize: "clamp(1.75rem,4vw,2.5rem)", fontWeight: 800, marginBottom: 12 }}>{title}</h2>
          {subtitle && <p style={{ color: "#6b7280" }}>{subtitle}</p>}
        </div>
        <form
          action={action}
          method="post"
          onSubmit={
            onSubmit ??
            ((e) => {
              e.preventDefault()
            })
          }
        >
          {(fields || []).map((f: FormField, i: number) =>
            f.type === "consent" ? (
              <div key={i} style={{ marginBottom: 16 }}>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, lineHeight: 1.5, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    name={f.label}
                    required={f.required === "yes"}
                    defaultChecked={false}
                    style={{ marginTop: 3, width: 16, height: 16, accentColor: "var(--site-accent, #7c3aed)" }}
                  />
                  <span>
                    {f.label}
                    {f.required === "yes" && <span style={{ color: "#ef4444" }}> *</span>}
                  </span>
                </label>
              </div>
            ) : (
              <div key={i} style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                  {f.label}
                  {f.required === "yes" && <span style={{ color: "#ef4444" }}> *</span>}
                </label>
                {f.type === "textarea" ? (
                  <textarea rows={4} name={f.label} required={f.required === "yes"} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 15, boxSizing: "border-box" }} />
                ) : (
                  <input type={f.type} name={f.label} required={f.required === "yes"} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 15, boxSizing: "border-box" }} />
                )}
              </div>
            )
          )}
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: "100%",
              padding: 12,
              background: "var(--site-accent, #7c3aed)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              cursor: submitting ? "wait" : "pointer",
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? "…" : submitLabel}
            {status === "success" && " ✓"}
          </button>
          {status === "error" && (
            <p style={{ marginTop: 12, fontSize: 13, color: "#ef4444", textAlign: "center" }}>
              Something went wrong. Please try again.
            </p>
          )}
        </form>
        {successMessage && <p style={{ marginTop: 12, fontSize: 13, color: "#6b7280", textAlign: "center" }}>{successMessage}</p>}
      </div>
    </section>
  )
}

